import toast from "react-hot-toast";
import {
  ICartBarcodeValue,
  ICartSearchItemClickResponseData,
  ICartSearchResponseData,
} from "@/api_framework/api_modals/Cart";
import { ICartItemDetailsPayload } from "@/app/hooks/api_hooks/Cart/useCartSearchItemClickAPI";
import { ICartItemAddModalState } from "../../../../modals/CartItemAddModal";
import { IBarcodeValidationContext } from "./useBarcodeValidationState";

interface UseCartLotDetailsFlowParams {
  activeCartId: string;
  mappedClinicId: string;
  customerId: string;
  petId: string;
  currentAddress: any;
  activeCartDetails: any;
  cartLevelCouponId: string;
  isClinicOpsUser: boolean;
  fetchProductDetails: (payload: ICartItemDetailsPayload) => Promise<any> | undefined;
  updateCart: (payload: any, mode: "normal" | "hd") => Promise<any>;
  deleteCartItem: (payload: any, mode: "normal" | "hd") => Promise<any>;
  refetchCartDetails: (...args: any[]) => Promise<any>;
  setModalData: (data: ICartItemAddModalState) => void;
  setVisible: () => void;
  pendingBarcodeValidation: IBarcodeValidationContext | null;
  barcodeInputValue: string;
  isBarcodeSlideoverOpen: boolean;
  setShowBarcodeMismatchError: (value: boolean) => void;
  openBarcodeValidationSlideover: (context: IBarcodeValidationContext) => void;
  closeBarcodeValidationSlideover: () => void;
}

// Groups the cart lot-details / barcode-validation flow: fetching product lot details for an
// item, resolving single vs multi variant results into an updateCart call, and the barcode
// scan-mismatch guard that can intercept it before the update happens.
export const useCartLotDetailsFlow = ({
  activeCartId,
  mappedClinicId,
  customerId,
  petId,
  currentAddress,
  activeCartDetails,
  cartLevelCouponId,
  isClinicOpsUser,
  fetchProductDetails,
  updateCart,
  deleteCartItem,
  refetchCartDetails,
  setModalData,
  setVisible,
  pendingBarcodeValidation,
  barcodeInputValue,
  isBarcodeSlideoverOpen,
  setShowBarcodeMismatchError,
  openBarcodeValidationSlideover,
  closeBarcodeValidationSlideover,
}: UseCartLotDetailsFlowParams) => {
  const normalizeBarcode = (value: unknown) => {
    if (typeof value === "string" || typeof value === "number") {
      return String(value).trim();
    }
    return "";
  };

  const normalizeBarcodeList = (value?: ICartBarcodeValue | unknown): string[] => {
    if (Array.isArray(value)) {
      const normalizedBarcodes: string[] = [];
      for (const barcode of value) {
        const normalizedBarcode = normalizeBarcode(barcode);
        if (normalizedBarcode?.length > 0) {
          normalizedBarcodes?.push(normalizedBarcode);
        }
      }
      return normalizedBarcodes;
    }
    const normalizedValue = normalizeBarcode(value);
    return normalizedValue ? [normalizedValue] : [];
  };

  const extractItemBarcodeSet = (item?: ICartSearchResponseData | null) => {
    const combinedBarcodes = [...normalizeBarcodeList(item?.barcode), ...normalizeBarcodeList(item?.bar_code)];
    return new Set(combinedBarcodes);
  };

  const shouldRequireBarcodeValidation = (item?: ICartSearchResponseData | null) => {
    return extractItemBarcodeSet(item).size > 0;
  };

  const showBarcodeMismatch = () => {
    if (isBarcodeSlideoverOpen) {
      setShowBarcodeMismatchError(true);
      return;
    }
  };

  const getDewormingItemFlag = (selectedItem: ICartSearchResponseData): boolean | undefined => {
    if (typeof selectedItem?.is_deworming_item === "boolean") {
      return selectedItem.is_deworming_item;
    }
    if (typeof selectedItem?.is_deworming === "boolean") {
      return selectedItem.is_deworming;
    }
    return undefined;
  };

  const constructProductDetailsPayload = (
    item: ICartSearchResponseData,
    applyMembershipCoupons?: boolean,
    editLotDetails?: boolean,
  ): ICartItemDetailsPayload => {
    const dewormingFlag = getDewormingItemFlag(item);

    let payload: ICartItemDetailsPayload = {
      availability: item?.stock_qty_with_text ?? "available",
      cart_id: activeCartId,
      clinic_id: mappedClinicId,
      id: item.id,
      pet_id: item?.pet_id || petId,
      selected_item: item?.tag,
      is_hd_stock: item?.is_hd_stock,
      customer_id: customerId,
      unit_price: item?.price,
      pincode: currentAddress?.pincode || "",
      ...(typeof dewormingFlag === "boolean" ? { is_deworming_item: dewormingFlag } : {}),
    };
    if (applyMembershipCoupons) {
      payload = { ...payload, apply_membership_coupons: true };
    }
    if (editLotDetails) {
      payload = { ...payload, edit_lot_details: true };
    }
    if (item?.gift_id) {
      // A gift product is inserted straight away (gift coupon, no lot pick) unless its lots are being edited
      payload = {
        ...payload,
        gift_id: item.gift_id,
        reference_id: item.reference_id,
        // The line's coupon from the cart (the gift's), when its lots are edited
        ...(item?.coupon_id ? { coupon_id: item.coupon_id } : {}),
        ...(item?.tag === "#product" && !editLotDetails ? { edit_lot_details: false } : {}),
      };
    }
    return payload;
  };

  const getAutoSelectQty = (autoSelectValue: number): number | null => {
    if (!Number.isFinite(autoSelectValue) || autoSelectValue <= 0) {
      return null;
    }
    return Math.floor(autoSelectValue);
  };

  const applyAutoSelectToProductDetails = (details: ICartSearchItemClickResponseData[] = []) => {
    return details.map((detail) => {
      const autoSelectQty = getAutoSelectQty(Number(detail?.auto_select));
      if (autoSelectQty === null) {
        return detail;
      }
      return {
        ...detail,
        qty: autoSelectQty,
      };
    });
  };

  const buildMainCartItemsPayload = () =>
    activeCartDetails?.hd_cart?.main_cart_items?.map((mainItem) => ({
      vetic_variant_id: mainItem?.vetic_variant_id,
      unique_row_id: mainItem?.unique_row_id,
      quantity: mainItem?.quantity,
      unit_price: mainItem?.unit_price,
      coupon_id: mainItem?.coupon_id ?? "",
    })) ?? [];

  const buildCartUpdateItem = (
    item: ICartSearchResponseData,
    id: string,
    qty: number,
    uniqueRowId: string | undefined,
    lotDetails: any,
    dewormingItemFlag: boolean | undefined,
    lot?: Partial<ICartSearchItemClickResponseData>,
  ) => {
    // A gift line keeps its gift, booking and the gift's coupon across a lot update
    const giftId = lot?.gift_id || item?.gift_id;
    return {
      source: 1,
      id,
      qty,
      coupon_id: giftId ? lot?.coupon_id || item?.coupon_id || "" : "",
      tag: item?.tag,
      availability: item?.stock_qty_with_text ?? "",
      unique_row_id: uniqueRowId,
      lot_details: lotDetails ?? null,
      ...(typeof dewormingItemFlag === "boolean" ? { is_deworming_item: dewormingItemFlag } : {}),
      ...(giftId ? { gift_id: giftId, reference_id: lot?.reference_id || item?.reference_id || undefined } : {}),
    };
  };

  const buildUpdateCartPayload = (item: ICartSearchResponseData, petItems: any[]) => {
    const targetPetId = item?.pet_id || petId;
    return {
      customer_id: customerId,
      pincode: currentAddress?.pincode,
      cart_id: activeCartId,
      clinic_id: mappedClinicId,
      pets: [
        {
          pet_id: targetPetId,
          items: petItems,
          sync_version: activeCartDetails?.pets?.find((p: any) => p.pet_id === targetPetId)?.sync_version,
        },
      ],
      main_cart_items: buildMainCartItemsPayload(),
      is_hd_stock: item?.is_hd_stock,
    };
  };

  const deleteSwappedCartItem = (
    item: ICartSearchResponseData & { rowId?: string; urid?: string },
    mode: "normal" | "hd",
  ) =>
    deleteCartItem(
      {
        cart_id: activeCartId,
        item_id: (item as any).rowId,
        pet_id: item?.pet_id || petId,
        unique_row_id: (item as any).urid,
      },
      mode,
    );

  const finalizeCartUpdateWithRefetch = (
    swap: boolean,
    item: ICartSearchResponseData,
    mode: "normal" | "hd",
    successMessage: string,
  ) => {
    const refetch = () => refetchCartDetails(activeCartId, mappedClinicId, mode, cartLevelCouponId, currentAddress?.pincode);
    if (swap) {
      return deleteSwappedCartItem(item, mode).then(() => {
        toast.success("Lot Details Have Been Updated Successfully.");
        refetch();
      });
    }
    toast.success(successMessage);
    return refetch();
  };

  const finalizeCartUpdateNoRefetch = async (
    swap: boolean,
    item: ICartSearchResponseData,
    mode: "normal" | "hd",
    successMessage: string,
  ) => {
    if (swap) {
      await deleteSwappedCartItem(item, mode);
      toast.success("Lot Details Have Been Updated Successfully.");
      return;
    }
    toast.success(successMessage);
  };

  const fetchLotDetails = (
    item: ICartSearchResponseData,
    swap: boolean = false,
    applyMembershipCoupons?: boolean,
    editLotDetails?: boolean,
    isRefreshCart?: boolean,
    scannedBarcode?: string,
    skipBarcodeValidation?: boolean,
  ) => {
    const dewormingItemFlag = getDewormingItemFlag(item);
    const itemBarcodeSet = extractItemBarcodeSet(item);
    const normalizedScannedBarcode = normalizeBarcode(scannedBarcode);

    if (isClinicOpsUser && !swap && !editLotDetails && !skipBarcodeValidation && shouldRequireBarcodeValidation(item)) {
      openBarcodeValidationSlideover({
        item,
        swap,
        applyMembershipCoupons,
        editLotDetails,
        isRefreshCart,
      });
      return;
    }
    if (
      !swap &&
      !editLotDetails &&
      normalizedScannedBarcode &&
      itemBarcodeSet.size > 0 &&
      !itemBarcodeSet.has(normalizedScannedBarcode)
    ) {
      showBarcodeMismatch();
      return;
    }

    const payload = constructProductDetailsPayload(item, applyMembershipCoupons, editLotDetails);
    return fetchProductDetails(payload)?.then(async (productDetails) => {
      if (!swap && !editLotDetails && normalizedScannedBarcode) {
        const variantBarcodeSet = new Set<string>();
        if (Array.isArray(productDetails)) {
          for (const detail of productDetails) {
            const detailBarcodes = [
              ...normalizeBarcodeList(detail?.barcode),
              ...normalizeBarcodeList(detail?.bar_code),
            ];
            for (const barcode of detailBarcodes) {
              variantBarcodeSet.add(barcode);
            }
          }
        }
        if (variantBarcodeSet.size > 0 && !variantBarcodeSet.has(normalizedScannedBarcode)) {
          showBarcodeMismatch();
          return;
        }
      }
      const normalizedProductDetails = applyAutoSelectToProductDetails(
        (Array.isArray(productDetails) ? productDetails : []) as ICartSearchItemClickResponseData[],
      );
      await refetchCartDetails(
        activeCartId,
        mappedClinicId,
        "hd",
        cartLevelCouponId,
        currentAddress?.pincode,
        null,
        isRefreshCart,
      );
      // A gift product is added by item-search-click itself (no lot pick), so it returns no lot details
      if (item?.tag === "#product" && !item?.gift_id && normalizedProductDetails?.length === 0) {
        toast.error("Product has lot details missing.");
      }
      if (normalizedProductDetails?.length > 0) {
        if (normalizedProductDetails?.length === 1) {
          if (isRefreshCart) {
            return;
          }
          const details = normalizedProductDetails[0];
          const selectedQty = getAutoSelectQty(Number(details?.auto_select)) ?? 1;
          return updateCart(
            buildUpdateCartPayload(item, [
              buildCartUpdateItem(
                item,
                details?.id,
                selectedQty,
                details?.unique_row_id,
                details?.lot_details,
                dewormingItemFlag,
                details,
              ),
            ]),
            "normal",
          ).then((res) => {
            // Stale-screen rejection -> resync WITHOUT running finalize's paired delete (it would
            // drop the old line even though the new line was rejected). A successful PUT returns
            // null data, so key off the explicit marker, never falsiness.
            if (res?.stale_sync_version) {
              refetchCartDetails(activeCartId, mappedClinicId, "normal", cartLevelCouponId, currentAddress?.pincode);
              return;
            }
            finalizeCartUpdateWithRefetch(swap, item, "normal", `${details.name} added to cart successfully.`);
          });
        } else {
          setModalData({
            item: item,
            cartVariants: normalizedProductDetails,
            onItemsAdd: (items) => {
              if (items?.length > 0) {
                updateCart(
                  buildUpdateCartPayload(
                    item,
                    items.map((i) =>
                      buildCartUpdateItem(item, i.variant_id, i.qty, i.unique_row_id, i?.lot_details, dewormingItemFlag, i),
                    ),
                  ),
                  "hd",
                ).then((res) => {
                  // Stale-screen rejection -> resync, skip finalize's paired delete. See note above.
                  if (res?.stale_sync_version) {
                    refetchCartDetails(activeCartId, mappedClinicId, "hd", cartLevelCouponId, currentAddress?.pincode);
                    return;
                  }
                  finalizeCartUpdateWithRefetch(swap, item, "hd", "Items added into cart successfully.");
                });
              }
            },
          } as ICartItemAddModalState);
          setVisible();
        }
      }
    });
  };

  const handleBarcodeValidateAndAdd = () => {
    if (!pendingBarcodeValidation?.item) return;
    const scannedBarcode = normalizeBarcode(barcodeInputValue);
    const itemBarcodeSet = extractItemBarcodeSet(pendingBarcodeValidation.item);
    if (!scannedBarcode) {
      return;
    }
    if (itemBarcodeSet.size === 0) {
      closeBarcodeValidationSlideover();
      fetchLotDetails(
        pendingBarcodeValidation.item,
        pendingBarcodeValidation.swap,
        pendingBarcodeValidation.applyMembershipCoupons,
        pendingBarcodeValidation.editLotDetails,
        pendingBarcodeValidation.isRefreshCart,
      );
      return;
    }
    if (!itemBarcodeSet.has(scannedBarcode)) {
      setShowBarcodeMismatchError(true);
      return;
    }
    closeBarcodeValidationSlideover();
    fetchLotDetails(
      pendingBarcodeValidation.item,
      pendingBarcodeValidation.swap,
      pendingBarcodeValidation.applyMembershipCoupons,
      pendingBarcodeValidation.editLotDetails,
      pendingBarcodeValidation.isRefreshCart,
      scannedBarcode,
      true,
    );
  };

  const handlePetUpdateRetrySuccess = async (
    item: ICartSearchResponseData,
    productDetails: any[],
    swap: boolean = false,
  ) => {
    const dewormingItemFlag = getDewormingItemFlag(item);
    const normalizedProductDetails = applyAutoSelectToProductDetails(
      (Array.isArray(productDetails) ? productDetails : []) as ICartSearchItemClickResponseData[],
    );
    if (!normalizedProductDetails) return;
    if (normalizedProductDetails?.length === 0) {
      toast.error("Product has lot details missing.");
      return;
    }
    if (normalizedProductDetails?.length === 1) {
      const details = normalizedProductDetails[0];
      const selectedQty = getAutoSelectQty(Number(details?.auto_select)) ?? 1;
      const singleRes = await updateCart(
        buildUpdateCartPayload(item, [
          buildCartUpdateItem(item, details?.id, selectedQty, details?.unique_row_id, details?.lot_details, dewormingItemFlag),
        ]),
        "normal",
      );
      // Stale-screen rejection -> resync, skip the paired delete (avoid dropping the old line).
      if (singleRes?.stale_sync_version) {
        await refetchCartDetails(activeCartId, mappedClinicId, "normal", cartLevelCouponId, currentAddress?.pincode);
        return;
      }
      await finalizeCartUpdateNoRefetch(swap, item, "normal", `${details.name} added to cart successfully.`);
      return;
    }
    setModalData({
      item: item,
      cartVariants: normalizedProductDetails,
      onItemsAdd: async (items) => {
        if (items?.length > 0) {
          const multiRes = await updateCart(
            buildUpdateCartPayload(
              item,
              items.map((i) =>
                buildCartUpdateItem(item, i.variant_id, i.qty, i.unique_row_id, i?.lot_details, dewormingItemFlag),
              ),
            ),
            "hd",
          );
          // Stale-screen rejection -> resync, skip the paired delete.
          if (multiRes?.stale_sync_version) {
            await refetchCartDetails(activeCartId, mappedClinicId, "hd", cartLevelCouponId, currentAddress?.pincode);
            return;
          }
          await finalizeCartUpdateNoRefetch(swap, item, "hd", "Items added into cart successfully.");
        }
      },
    } as ICartItemAddModalState);
    setVisible();
  };

  return { fetchLotDetails, handleBarcodeValidateAndAdd, handlePetUpdateRetrySuccess };
};
